import React, {createContext, useCallback, useContext, useRef} from 'react';

type ServerAbortRequestsProviderContext = {
  getAbortController: () => AbortController | undefined;
  abortAllRequests: () => void;
};

const ServerAbortRequestsProviderContext =
  createContext<ServerAbortRequestsProviderContext>({
    getAbortController: () => {
      throw new Error(
        'Default value should never be used, make sure ServerAbortRequestsProviderContext is used',
      );
    },
    abortAllRequests: () => {
      throw new Error(
        'Default value should never be used, make sure ServerAbortRequestsProviderContext is used',
      );
    },
  });

export const ServerAbortRequestsProvider: React.FC<
  React.PropsWithChildren<{enableAbortRequests?: boolean}>
> = ({children, enableAbortRequests}) => {
  const abortController = useRef<AbortController | null>(null);

  const abortAllRequests = useCallback(() => {
    if (process.browser) return;
    if (!enableAbortRequests) return;

    if (abortController.current && !abortController.current.signal.aborted) {
      abortController.current.abort();
      abortController.current = null;
    }
  }, [enableAbortRequests]);

  const getAbortController = useCallback(() => {
    if (process.browser) return;
    if (!enableAbortRequests) return;

    if (!abortController.current) {
      abortController.current = new AbortController();
    }

    return abortController.current;
  }, [enableAbortRequests]);

  const value: ServerAbortRequestsProviderContext = {
    getAbortController,
    abortAllRequests,
  };

  return (
    <ServerAbortRequestsProviderContext.Provider value={value}>
      {children}
    </ServerAbortRequestsProviderContext.Provider>
  );
};

export const useServerAbortRequests = () => {
  return useContext(ServerAbortRequestsProviderContext);
};
